import { useCallback } from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import { isUIAnalyticsEvent } from '@atlaskit/analytics-next/isUIAnalyticsEvent';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { type AIEventBaseAttributes, useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { addSentryBreadcrumb } from '../../services/sentry';

import { useAnalyticsPropertiesContext } from './analytics-properties-context';
import { AI_MATE_ATTRIBUTES, type AIMateAttributeType } from './analytics-properties-context/types';
import { ANALYTICS_CHANNEL } from './constants';
import {
	type AIMAUEventPayload,
	type IUseAnalytics,
	type SendAnalyticsEvent,
	type TrackAIActioned,
	type TrackAIAdopted,
	type TrackAIEventPayloads,
	type TrackAIFeedback,
} from './types';
import { getAttributesFromContexts } from './utils';

const isTownSquareEvent = () =>
	['home.atlassian.com', 'team.atlassian.com'].some((host) =>
		window.location?.host?.includes(host),
	);

const DEFAULT_TAGS: string[] = ['atlaskit', 'aiMate'];
// Single allowlist check for both event shapes this hook fires: regular analytics events
// (`action`/`actionSubject`) and unified AIMAU track events (`type`). Exported so callers that
// source rovoJourneyId by an explicit conversation ID rather than from this hook's own context
// (e.g. sendMessageActionProvider, for in-flight sends after the user switches conversations)
// can reuse this instead of re-declaring their own allowlist.
export const isRovoJourneyAllowlistedEvent = ({
	action,
	actionSubject,
	type,
}: {
	action?: string;
	actionSubject?: string;
	type?: string;
}): boolean =>
	// message sent (client) — sendAnalyticsEvent only, no trackAIMAUEvent equivalent.
	(action === 'sent' && actionSubject === 'message') ||
	// aiMateActions displayed (client) — sendAnalyticsEvent only, no trackAIMAUEvent equivalent.
	(action === 'displayed' && actionSubject === 'aiMateActions') ||
	// aiInteraction initiated — via sendAnalyticsEvent or trackAIMAUEvent({ type: 'initiated' }).
	(action === 'initiated' && actionSubject === 'aiInteraction') ||
	type === 'initiated' ||
	// aiResult viewed — via sendAnalyticsEvent or trackAIMAUEvent({ type: 'viewed' }).
	(action === 'viewed' && actionSubject === 'aiResult') ||
	type === 'viewed' ||
	// aiResult actioned (client) — via sendAnalyticsEvent or trackAIMAUEvent({ type: 'actioned' }).
	(action === 'actioned' && actionSubject === 'aiResult') ||
	type === 'actioned';

const getRovoJourneyEventAttributes = ({
	eventPayload,
	attributes,
	contextRovoJourneyId,
}: {
	eventPayload: { action?: string; actionSubject?: string };
	attributes: Record<string, unknown>;
	contextRovoJourneyId?: string;
}) => {
	const {
		[AI_MATE_ATTRIBUTES.rovoJourneyId]: suppliedRovoJourneyId,
		...attributesWithoutRovoJourneyId
	} = attributes;
	const rovoJourneyId = suppliedRovoJourneyId ?? contextRovoJourneyId;

	return {
		...attributesWithoutRovoJourneyId,
		...(isRovoJourneyAllowlistedEvent(eventPayload) && rovoJourneyId
			? { [AI_MATE_ATTRIBUTES.rovoJourneyId]: rovoJourneyId }
			: {}),
	};
};

const SENTRY_ANALYTICS_ATTRIBUTE_KEYS = [
	'experience',
	'nextState',
	'outcome',
	'previousState',
	'screen',
	'state',
	'surface',
	'touchpoint',
] as const;

const getSentryAnalyticsBreadcrumb = (event: UIAnalyticsEvent) => {
	const payload = event.payload;
	const attributes = payload?.attributes ?? {};
	const safeAttributes = SENTRY_ANALYTICS_ATTRIBUTE_KEYS.reduce<
		Record<string, string | number | boolean>
	>((result, key) => {
		const value = attributes[key];
		if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
			result[key] = value;
		}
		return result;
	}, {});

	return {
		category: 'analytics',
		data: {
			action: payload?.action,
			actionSubject: payload?.actionSubject,
			actionSubjectId: payload?.actionSubjectId,
			...safeAttributes,
		},
		message: [payload?.action, payload?.actionSubject, payload?.actionSubjectId]
			.filter(Boolean)
			.join(' '),
	};
};

/**
 * A wrapper around the analytics-next hook to send analytics events to the ai mate channel
 */
export const useAnalytics = (options?: { skipAISessionValidation?: boolean }): IUseAnalytics => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { propertiesRef, mode } = useAnalyticsPropertiesContext();

	// TODO: Improve this functionality, potentially with a higher order provider for tags or similar
	// This is a temporary solution to add the 'townsquare' tag to operational events so Experience Tracker works in Atlas
	const maybeAtlasOperationalTags = useCallback(
		(inputPayload: AnalyticsEventPayload | UIAnalyticsEvent) => {
			const payload = isUIAnalyticsEvent(inputPayload) ? inputPayload.payload : inputPayload;
			if (
				payload.actionSubject === 'ui' &&
				payload.eventType === 'operational' &&
				isTownSquareEvent()
			) {
				return ['townsquare'];
			}
			return [];
		},
		[],
	);

	const tagsForAIAnalytics = [...DEFAULT_TAGS];
	if (isTownSquareEvent()) {
		tagsForAIAnalytics.push('townsquare');
	}
	const {
		trackAIInteractionInit,
		trackAIResultView,
		trackAIResultError,
		trackAIInteractionDismiss,
		trackAIResultAction,
		trackAIResultAdopted,
		trackAIFeedbackSubmit,
	} = useAIEventsInstrumentation({
		// Required configruation
		product: propertiesRef.current.product,
		subproduct: propertiesRef.current.product,
		aiFeatureName: 'aiChat',
		proactiveGeneratedAI: 0,
		userGeneratedAI: 1,
		enablePlatformEvents: true,
		tags: tagsForAIAnalytics,
		skipAISessionValidation: options?.skipAISessionValidation,
		customAnalyticsFire: (rawEvent, _createdEvent) => {
			// Build event and apply context attributes
			const event = createAnalyticsEvent(rawEvent);
			if (!event) {
				// In SSR / no-listener environments, `createAnalyticsEvent` can return `undefined`.
				// Nothing to fire.
				return;
			}
			const aiMateContextAttributes = getAttributesFromContexts(event.context);

			// Fire. `event.update(...)` returns `undefined` for stub events (no `<AnalyticsListener>`
			// mounted, e.g. during SSR), so fall back to the original event and guard `.fire` with
			// optional chaining.
			const updatedEvent =
				event.update({
					attributes: getRovoJourneyEventAttributes({
						eventPayload: event.payload ?? rawEvent,
						attributes: {
							...(event.payload?.attributes ?? {}),
							...aiMateContextAttributes,
						},
						contextRovoJourneyId: propertiesRef.current.rovoJourneyId,
					}),
				}) ?? event;
			updatedEvent?.fire?.(ANALYTICS_CHANNEL);
		},
	});

	const getDefaultAttributes = useCallback((): AIMateAttributeType => {
		const {
			agentCreatorType,
			agentId,
			agentVisibility,
			agentExternalConfigReference,
			agentActions,
			agentIsDefault,
			agentIsVerified,
			conversationChannelId,
			chatEntryPoint,
			experienceId,
			fullScreen,
			product,
			productHeader,
			inProductExperience,
			mode,
			assistanceServiceConfigMinionAlias,
			spaceId,
			isThirdPartyPromptConversation,
			thirdPartyPromptCohort,
			thirdPartyConnectCount,
		} = propertiesRef.current;
		return {
			[AI_MATE_ATTRIBUTES.agentCreatorType]: agentCreatorType,
			[AI_MATE_ATTRIBUTES.agentId]: agentId,
			[AI_MATE_ATTRIBUTES.agentVisibility]: agentVisibility,
			[AI_MATE_ATTRIBUTES.conversationChannelId]: conversationChannelId,
			[AI_MATE_ATTRIBUTES.chatEntryPoint]: chatEntryPoint,
			[AI_MATE_ATTRIBUTES.experienceId]: experienceId,
			[AI_MATE_ATTRIBUTES.fullScreen]: fullScreen,
			[AI_MATE_ATTRIBUTES.product]: product,
			[AI_MATE_ATTRIBUTES.productHeader]: productHeader,
			[AI_MATE_ATTRIBUTES.inProductExperience]: inProductExperience,
			[AI_MATE_ATTRIBUTES.singleInstrumentationID]: conversationChannelId,
			[AI_MATE_ATTRIBUTES.agentExternalConfigReference]: agentExternalConfigReference,
			[AI_MATE_ATTRIBUTES.agentActions]: agentActions,
			[AI_MATE_ATTRIBUTES.agentIsDefault]: agentIsDefault,
			[AI_MATE_ATTRIBUTES.agentIsVerified]: agentIsVerified,
			[AI_MATE_ATTRIBUTES.mode]: mode,
			[AI_MATE_ATTRIBUTES.assistanceServiceConfigMinionAlias]: assistanceServiceConfigMinionAlias,
			[AI_MATE_ATTRIBUTES.spaceId]: spaceId,
			// Gated on TRUE, not merely defined: the modal stamps `false` on every submit to clear
			// a previous 3P conversation, so a defined-check would let every later conversation
			// carry these keys with two `undefined`s.
			...(isThirdPartyPromptConversation
				? {
						[AI_MATE_ATTRIBUTES.isThirdPartyPromptConversation]: isThirdPartyPromptConversation,
						[AI_MATE_ATTRIBUTES.thirdPartyPromptCohort]: thirdPartyPromptCohort,
						[AI_MATE_ATTRIBUTES.thirdPartyConnectCount]: thirdPartyConnectCount,
					}
				: {}),
		};
	}, [propertiesRef]);

	const trackAIMAUEvent = useCallback(
		(payload: TrackAIEventPayloads) => {
			// Handle tags
			const tags = [...DEFAULT_TAGS];
			if (isTownSquareEvent()) {
				tags.push('townsquare');
			}

			const {
				source: sourceUnknown,
				subjectId: subjectIdUnknown,
				proactive,
				userGenerated,
				doesNotMeetMAUCriteria,
				messageId,
				messageActions,
				singleInstrumentationID,
				deepResearchEnabled,
				webSearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				timestamp,
				citedProductList,
				citedProduct,
				connectorInfo,
				sourceProducts,
				sourceProduct,
				pluginsInvoked,
				tools,
				skillsInvoked,
				executedStratusSkill,
				responseSources,
				usesJsm,
				invokedFrom,
				conversationChannelId,
				assistanceServiceConfigMinionAlias,
				scenarioId,
				pageContext,
				viewContext,
				attributes: customAttributes,
			} = payload;
			// source and subjectId are optional in the payload type but trackAI* functions require string.
			// The runtime value (including undefined) is intentionally passed through as-is.
			// eslint-disable-next-line @atlaskit/platform/no-double-type-assertions -- Existing analytics baseline: preserves the runtime value while satisfying a string-only downstream API type.
			const source = sourceUnknown as unknown as string;
			// eslint-disable-next-line @atlaskit/platform/no-double-type-assertions -- Existing analytics baseline: preserves the runtime value while satisfying a string-only downstream API type.
			const subjectId = subjectIdUnknown as unknown as string;
			const customAttributesRecord = (customAttributes ?? {}) as Record<string, unknown>;
			const {
				[AI_MATE_ATTRIBUTES.rovoJourneyId]: customRovoJourneyId,
				...customAttributesWithoutRovoJourneyId
			} = customAttributesRecord;
			const contextRovoJourneyId = propertiesRef.current.rovoJourneyId;
			let attributes = {
				...getDefaultAttributes(),
				isAIFeature: 1,
				proactiveAIGenerated: proactive ? 1 : 0,
				userGeneratedAI: userGenerated ? 1 : 0,
				citedProductList,
				citedProduct,
				sourceProducts,
				sourceProduct,
				...customAttributesWithoutRovoJourneyId,
				...(isRovoJourneyAllowlistedEvent(payload) && (customRovoJourneyId || contextRovoJourneyId)
					? {
							[AI_MATE_ATTRIBUTES.rovoJourneyId]: customRovoJourneyId ?? contextRovoJourneyId,
						}
					: {}),
				aiFeatureName: (customAttributesRecord.aiFeatureName as string) || 'aiChat',
			} as AIMAUEventPayload['attributes'];

			if (messageId) {
				attributes.messageId = messageId;
			}

			if (messageActions) {
				attributes.messageActions = messageActions;
			}

			if (invokedFrom) {
				attributes.invokedFrom = invokedFrom;
			}

			if (timestamp) {
				attributes.timestamp = timestamp;
			}

			if (singleInstrumentationID) {
				attributes.singleInstrumentationID = singleInstrumentationID;
			}

			if (pluginsInvoked) {
				attributes.pluginsInvoked = pluginsInvoked;
			}

			if (tools) {
				attributes.tools = tools;
			}

			if (skillsInvoked) {
				attributes.skillsInvoked = skillsInvoked;
			}

			if (executedStratusSkill !== undefined) {
				attributes.executedStratusSkill = executedStratusSkill;
			}

			if (responseSources) {
				attributes.responseSources = responseSources;
			}

			if (
				connectorInfo &&
				(payload.type === 'actioned'
					? fg('rovo_chat_connector_info_analytics_on_click')
					: fg('rovo_chat_connector_info_analytics'))
			) {
				attributes.connectorInfo = connectorInfo;
			}

			if (usesJsm !== undefined) {
				attributes.usesJsm = usesJsm;
			}

			if (deepResearchEnabled) {
				attributes.deepResearchEnabled = deepResearchEnabled;
			}

			if (fastModeEnabled) {
				attributes.fastModeEnabled = fastModeEnabled;
			}

			if (thinkDeeperEnabled) {
				attributes.thinkDeeperEnabled = thinkDeeperEnabled;
			}

			if (webSearchEnabled) {
				attributes.webSearchEnabled = webSearchEnabled;
			}

			if (conversationChannelId) {
				attributes.conversationChannelId = conversationChannelId;
			}

			if (assistanceServiceConfigMinionAlias) {
				attributes.assistanceServiceConfigMinionAlias = assistanceServiceConfigMinionAlias;
			}
			if (pageContext) {
				attributes.pageContext = pageContext;
			}

			if (scenarioId) {
				attributes.scenarioId = scenarioId;
			}

			if (viewContext) {
				attributes.viewContext = viewContext;
			}

			let libAttributes: AIEventBaseAttributes = {
				...attributes,
				aiFeatureName: (customAttributesRecord.aiFeatureName as string) || 'aiChat',
				proactiveGeneratedAI: proactive ? 1 : 0,
			};

			const launchSource = propertiesRef.current.launchSource;
			const launchSourceAttribute = launchSource
				? { [AI_MATE_ATTRIBUTES.launchSource]: launchSource }
				: {};
			switch (payload.type) {
				case 'initiated':
					trackAIInteractionInit({
						attributes: libAttributes,
						tags,
						source: source,
						actionSubjectId: subjectId,
					});
					break;
				case 'viewed':
					trackAIResultView({
						attributes: libAttributes,
						tags,
						source: source,
						actionSubjectId: subjectId,
						doesNotMeetMAUCriteria,
					});
					break;
				case 'error':
					trackAIResultError(
						{ aiErrorMessage: payload.errorMessage as string },
						{
							attributes: libAttributes,
							tags,
							source: source,
							actionSubjectId: subjectId,
						},
					);
					break;
				case 'dismissed':
					trackAIInteractionDismiss({
						attributes: libAttributes,
						tags,
						source: source,
						actionSubjectId: subjectId,
					});
					break;
				case 'actioned': {
					const actionedPayload = payload as TrackAIActioned;
					trackAIResultAction(actionedPayload.actionResult, {
						attributes: {
							...libAttributes,
							...launchSourceAttribute,
						},
						tags,
						source: source,
						actionSubjectId: subjectId,
					});
					break;
				}
				case 'adopted': {
					const adoptedPayload = payload as TrackAIAdopted;
					trackAIResultAdopted(adoptedPayload.actionResult as string, {
						attributes: {
							...libAttributes,
							actionKey: adoptedPayload.actionKey as string,
							rovoModifiedObjects: adoptedPayload.rovoModifiedObjects,
							...launchSourceAttribute,
						},
						tags,
						source: source,
						actionSubjectId: subjectId,
					});
					break;
				}
				case 'feedback': {
					const feedbackPayload = payload as TrackAIFeedback;
					trackAIFeedbackSubmit(feedbackPayload.feedbackResult, {
						attributes: libAttributes,
						tags,
						source: source,
						actionSubjectId: subjectId,
					});
					break;
				}
			}
			// Handle specific AI MAU events
		},
		[
			getDefaultAttributes,
			propertiesRef,
			trackAIInteractionInit,
			trackAIResultView,
			trackAIResultError,
			trackAIInteractionDismiss,
			trackAIResultAction,
			trackAIResultAdopted,
			trackAIFeedbackSubmit,
		],
	);

	const sendAnalyticsEvent: SendAnalyticsEvent = useCallback(
		(payload, options) => {
			const extraTags = options?.removeDefaultTags ? [] : DEFAULT_TAGS;
			const allTags = Array.from(
				new Set([
					...extraTags,
					...('tags' in payload && Array.isArray(payload.tags) ? payload.tags : []),
					...maybeAtlasOperationalTags(payload),
				]),
			);

			const defaultAttributes = getDefaultAttributes();

			// `UIAnalyticsEvent | undefined` because in SSR (no `<AnalyticsListener>`),
			// `createAnalyticsEvent` and `event.update(...)` may return `undefined` stubs.
			let eventToBeLoggedWithSentry: UIAnalyticsEvent | undefined;
			if (isUIAnalyticsEvent(payload)) {
				const aiMateContextAttributes = getAttributesFromContexts(payload.context);

				// `payload.update(...)` can return `undefined` for stub events in SSR / no-listener
				// contexts. Fall back to the original event and guard `.fire` with optional chaining.
				const updatedEvent =
					payload.update({
						...payload.payload,
						tags: allTags,
						attributes: getRovoJourneyEventAttributes({
							eventPayload: payload.payload ?? {},
							attributes: {
								...defaultAttributes,
								// `payload.payload` is typed as defined on a UIAnalyticsEvent, but during SSR
								// (and other no-listener contexts) the underlying event can be a stub whose
								// `payload` is `undefined`. Guard the spread so the subscriber does not throw
								// `Cannot read properties of undefined (reading 'attributes')`.
								...(payload.payload?.attributes ?? {}),
								...aiMateContextAttributes,
							},
							contextRovoJourneyId: propertiesRef.current.rovoJourneyId,
						}),
					}) ?? payload;
				updatedEvent?.fire?.(ANALYTICS_CHANNEL);
				eventToBeLoggedWithSentry = updatedEvent;
			} else {
				const event = createAnalyticsEvent({
					...payload,
					tags: allTags,
					attributes: getRovoJourneyEventAttributes({
						eventPayload: payload,
						attributes: {
							...defaultAttributes,
							...payload.attributes,
						},
						contextRovoJourneyId: propertiesRef.current.rovoJourneyId,
					}),
				});

				if (!event) {
					// In SSR / no-listener environments, `createAnalyticsEvent` can return `undefined`.
					// Nothing to fire or log.
					return;
				}

				const aiMateContextAttributes = getAttributesFromContexts(event.context);
				// `event.update(...)` can return `undefined` for stub events in SSR / no-listener
				// contexts (previously crashed with `Cannot read properties of undefined (reading
				// 'fire')`). Fall back to the original event and guard `.fire` with optional chaining.
				const updatedEvent =
					event.update({
						attributes: getRovoJourneyEventAttributes({
							eventPayload: event.payload ?? payload,
							attributes: {
								// `event.payload` is normally defined when `createAnalyticsEvent` is wired up
								// via `<AnalyticsListener>`, but in SSR (where no listener is mounted) the
								// returned event can be a stub whose `payload` is `undefined`. Spreading
								// without a guard previously crashed the ExperienceTracker subscriber with
								// `Cannot read properties of undefined (reading 'attributes')`.
								...(event.payload?.attributes ?? {}),
								...aiMateContextAttributes,
							},
							contextRovoJourneyId: propertiesRef.current.rovoJourneyId,
						}),
					}) ?? event;
				updatedEvent?.fire?.(ANALYTICS_CHANNEL);
				eventToBeLoggedWithSentry = updatedEvent;
			}
			if (
				eventToBeLoggedWithSentry &&
				eventToBeLoggedWithSentry.payload?.eventType !== 'operational'
			) {
				// choosing to log the event with Sentry if it is not an operational event to prevent noise in Sentry and not eat up the Sentry quota
				addSentryBreadcrumb(getSentryAnalyticsBreadcrumb(eventToBeLoggedWithSentry));
			}
		},
		[createAnalyticsEvent, maybeAtlasOperationalTags, getDefaultAttributes, propertiesRef],
	);

	return {
		sendAnalyticsEvent,
		trackAIMAUEvent,
		mode,
	};
};
